import { useCallback, useEffect, useMemo } from 'react';

import { useHoverIntent } from './useHoverIntent';

import type { HighlightRange } from '@src/utility/transcriptQuoteUtils';
import type React from 'react';

const HIGHLIGHT_POPOVER_CLOSE_DELAY_MS = 100;
// Dwell before the action popover opens: a cursor sweeping across highlighted
// text (reading, or en route elsewhere) never pops the menu — only a cursor
// that rests on a highlight does. Range brightening stays instant (hoverEl).
const HIGHLIGHT_POPOVER_OPEN_DELAY_MS = 400;
const POPOVER_TEST_ID = 'transcript-highlight-action-popover';

// Underline hover-band (px), relative to a line's bottom edge. Extend up to
// catch the underline mark (baseline + offset/thickness) while staying below
// most glyphs, and down to catch the visible gap between transcript lines.
const UNDERLINE_HIT_UP = 6;
const UNDERLINE_HIT_DOWN = 22;

/**
 * Redline/diff view resolves overlays differently from the per-word views: the
 * underline is rendered as `OverlayUnderline` spans tagged with the overlay id
 * (`data-overlay-id`), because words are collapsed into block spans whose
 * `data-index` is only the block's FIRST word — useless for an overlay that
 * starts mid-block. Returns true when the cursor sits in the underline hover
 * band of any client rect of `span` (per-line, so multi-line spans work).
 */
function isInUnderlineBand(span: HTMLElement, clientY: number): boolean {
  return Array.from(span.getClientRects()).some(
    r =>
      clientY >= r.bottom - UNDERLINE_HIT_UP &&
      clientY <= r.bottom + UNDERLINE_HIT_DOWN,
  );
}

export type HighlightHoverInfo = {
  /** Bounding rect of the hovered span (in viewport coords). */
  anchorRect: DOMRect;
  /** Id of the quote whose range contains the hovered span, if any. */
  quoteId: string | null;
  /** Id of the clip whose range contains the hovered span, if any. */
  clipId: string | null;
  /**
   * PLAT-10045 — Id of the overlay composition whose time range contains
   * the hovered span, if any.
   */
  overlayId: string | null;
  /**
   * Id (epoch-ms key) of the saved segment whose range contains the hovered
   * span, if any — the save-for-later → combine flow.
   */
  savedSegmentId: string | null;
  /** Underlying hovered span element — caller passes through as anchor when opening downstream popovers. */
  anchorElement: HTMLElement;
  /** Caller calls this when the popover gains hover, to cancel the close timer. */
  onMouseEnter: () => void;
  /** Caller calls this when the popover loses hover, to re-arm the close timer. */
  onMouseLeave: () => void;
  /**
   * Enter overlay range-adjust mode for the hovered overlay (drag handles
   * on the underline). `undefined` when the hover is over a quote/clip-only
   * range, or when the consuming surface didn't supply `onBeginAdjust`
   * (e.g. translated view — range adjustment is original-language only).
   */
  onAdjustRange?: () => void;
};

/**
 * Given a span index, find the first range that contains it.
 *
 * Ranges are assumed sorted ascending by start; linear scan is fine for
 * small N (≤~50).
 *
 * **Overlap semantics — first-wins.** Overlay ranges can overlap on the
 * same span (two compositions covering the same words). The scan stops
 * at the first range whose `[start, end]` covers `idx`, which for
 * start-sorted input is the range with the earliest start. The popover
 * surfaces a single CTA per hover; the PLAT-10045 plan's open-questions
 * section tracks whether to disambiguate (innermost / 1-of-N indicator)
 * — v1 ships single-overlay UX. Quote and clip ranges are non-overlapping
 * by construction, so this only matters for overlays.
 *
 * Exported for unit tests — production callers always go through the
 * hook's `renderOverlay`, which composes quote/clip/overlay resolution
 * with the toggle gating.
 */
export function findRangeIdForIndex(
  idx: number,
  ranges: HighlightRange[],
): string | null {
  for (const r of ranges) {
    if (idx < r.start) return null;
    if (idx <= r.end) return r.id;
  }
  return null;
}

type UseHighlightHoverPopoverOpts = {
  showQuotes: boolean;
  showClips: boolean;
  /**
   * PLAT-10045 — overlays don't have a header toggle in v1; pass `true`
   * to enable the underline + hover popover for non-card composition
   * placements, `false` to skip.
   */
  showOverlays: boolean;
  /** Enable the underline + hover popover for saved segments. */
  showSavedSegments?: boolean;
  quoteRanges: HighlightRange[];
  clipRanges: HighlightRange[];
  overlayRanges: HighlightRange[];
  savedSegmentRanges?: HighlightRange[];
  getScrollContainer: () => HTMLElement | null | undefined;
  renderPopover: (info: HighlightHoverInfo) => React.ReactNode;
  /**
   * PLAT-10066 — the DOM attribute the hover logic reads to identify
   * spans. Defaults to `'data-index'` (original-language source-element
   * index). Pass `'data-initial-index'` for translated transcript views,
   * where spans are keyed by phrase-lead `initial_index`.
   *
   * Only one attribute is active per surface render — quote / clip ranges
   * aren't expected on the translated surface, so a single switch covers
   * the selector, gap-search, and `hoveredOverlayId` resolution paths.
   */
  spanIndexAttr?: string;
  /**
   * Redline/diff view only. When set (`'data-overlay-id'`), overlays are
   * resolved from the underline spans carrying that attribute rather than from
   * `spanIndexAttr` word indices — the redline DOM collapses words into block
   * spans, so an overlay starting mid-block can't be found by index. When
   * omitted (all per-word views), the index path is used unchanged.
   */
  overlayIdAttr?: string;
  /**
   * When provided, the rendered popover info exposes `onAdjustRange` for
   * the hovered overlay (the menu shows the "Adjust range" action). Omit
   * to hide the affordance — the consuming surface gates this on
   * original-language + an available commit path.
   */
  onBeginAdjust?: (overlayId: string) => void;
};

type UseHighlightHoverPopoverResult = {
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseDown: (e: React.MouseEvent) => void;
  /** Pass the event: a plain left click on a highlight opens the popover with no dwell. */
  onMouseUp: (e?: React.MouseEvent) => void;
  onMouseLeave: () => void;
  renderOverlay: () => React.ReactNode | null;
  /**
   * PLAT-10045 — the overlay range id the cursor is currently hovering
   * (within the underline hit-zone), or `null`. Consumers use this to
   * apply a brighter active class to spans inside the hovered range so
   * the whole range lights up — not the single hovered word and not all
   * overlay ranges in the document.
   */
  hoveredOverlayId: string | null;
};

/**
 * Composes useHoverIntent with highlight-specific logic to show a popover
 * when hovering over transcript spans that belong to quote/clip ranges.
 */
export function useHighlightHoverPopover(
  opts: UseHighlightHoverPopoverOpts,
): UseHighlightHoverPopoverResult {
  const {
    showQuotes,
    showClips,
    showOverlays,
    showSavedSegments = false,
    quoteRanges,
    clipRanges,
    overlayRanges,
    savedSegmentRanges = [],
    getScrollContainer,
    renderPopover,
    spanIndexAttr = 'data-index',
    overlayIdAttr,
    onBeginAdjust,
  } = opts;

  const selector = useCallback(
    (target: HTMLElement, event: React.MouseEvent): HTMLElement | null => {
      if (!showQuotes && !showClips && !showOverlays && !showSavedSegments)
        return null;
      // Redline/diff: resolve overlays from the `data-overlay-id` underline
      // spans (block spans can't address a mid-block overlay by index). Quotes
      // /clips don't render in this view, so this fully replaces the index
      // path. The underline band uses per-line client rects so multi-line
      // overlay spans trigger on every line, not just the last.
      if (overlayIdAttr && showOverlays) {
        let idSpan = target.closest<HTMLElement>(`[${overlayIdAttr}]`);
        if (!idSpan) {
          // Gap-search: cursor in the line-gap below the underline.
          for (const offset of [10, 18, 26]) {
            const el = document.elementFromPoint(
              event.clientX,
              event.clientY - offset,
            );
            const candidate =
              el instanceof HTMLElement
                ? el.closest<HTMLElement>(`[${overlayIdAttr}]`)
                : null;
            if (candidate && isInUnderlineBand(candidate, event.clientY)) {
              return candidate;
            }
          }
          return null;
        }
        return isInUnderlineBand(idSpan, event.clientY) ? idSpan : null;
      }
      const attrSelector = `[${spanIndexAttr}]`;
      let span = target.closest<HTMLElement>(attrSelector);
      // Gap-search: when the cursor is in the visible line-gap below an
      // overlay underline, the event target is the parent container (no
      // `data-index`). Sample a few pixels above the cursor to find the
      // nearest overlay span and treat it as the hover target so the
      // line lights up while the cursor is in the gap below it.
      let fromGapSearch = false;
      if (!span && showOverlays) {
        const GAP_MAX_PX = 30;
        const LOOKUP_OFFSETS = [10, 18, 26];
        for (const offset of LOOKUP_OFFSETS) {
          const el = document.elementFromPoint(
            event.clientX,
            event.clientY - offset,
          );
          const candidate =
            el instanceof HTMLElement
              ? el.closest<HTMLElement>(attrSelector)
              : null;
          if (!candidate) continue;
          const candidateIdx = Number(candidate.getAttribute(spanIndexAttr));
          if (Number.isNaN(candidateIdx)) continue;
          if (findRangeIdForIndex(candidateIdx, overlayRanges) === null)
            continue;
          const candidateRect = candidate.getBoundingClientRect();
          if (event.clientY - candidateRect.bottom > GAP_MAX_PX) continue;
          span = candidate;
          fromGapSearch = true;
          break;
        }
      }
      if (!span) return null;
      // PLAT-10045 — when the hovered span belongs ONLY to an overlay range
      // (no quote / clip overlap), gate the trigger to the underline strip
      // at the bottom of the word so reading-mouse-hovers don't pop the
      // pill.
      if (showOverlays && !fromGapSearch) {
        const idx = Number(span.getAttribute(spanIndexAttr));
        const overlayId = findRangeIdForIndex(idx, overlayRanges);
        const quoteId = showQuotes
          ? findRangeIdForIndex(idx, quoteRanges)
          : null;
        const clipId = showClips ? findRangeIdForIndex(idx, clipRanges) : null;
        const isOverlayOnly =
          !Number.isNaN(idx) &&
          overlayId !== null &&
          quoteId === null &&
          clipId === null;
        if (isOverlayOnly) {
          // Hover-zone for overlay-match words: a band that sits below
          // the word's letter bodies. Matches `overlayHighlightStyles`
          // (offset:3, thickness:2). For inline spans, `rect.bottom` is
          // the bottom of the LINE-BOX (includes line-height padding),
          // which sits a few px below the underline mark — so a
          // strictly-below-rect.bottom band misses the underline itself.
          // Extend up by 6px to catch the underline (baseline + 3..5)
          // while staying below baseline, where most letter glyphs end
          // (only g/p/q/y/j have descenders below it). Extend down by
          // 22px to catch the visible gap between transcript lines.
          const UNDERLINE_HIT_UP = 6;
          const UNDERLINE_HIT_DOWN = 22;
          const rect = span.getBoundingClientRect();
          if (event.clientY < rect.bottom - UNDERLINE_HIT_UP) return null;
          if (event.clientY > rect.bottom + UNDERLINE_HIT_DOWN) return null;
        }
      }
      return span;
    },
    [
      showQuotes,
      showClips,
      showOverlays,
      showSavedSegments,
      overlayRanges,
      quoteRanges,
      clipRanges,
      spanIndexAttr,
      overlayIdAttr,
    ],
  );

  const hover = useHoverIntent({
    delay: HIGHLIGHT_POPOVER_CLOSE_DELAY_MS,
    openDelay: HIGHLIGHT_POPOVER_OPEN_DELAY_MS,
    selector,
    scrollContainer: getScrollContainer,
    popoverSelector: `[data-testid="${POPOVER_TEST_ID}"]`,
  });

  // Dismiss when toggles turn off
  useEffect(() => {
    if (!showQuotes && !showClips && !showOverlays && !showSavedSegments) {
      hover.dismiss();
    }
  }, [showQuotes, showClips, showOverlays, showSavedSegments, hover.dismiss]);

  // Brightening feedback resolves from the instant hoverEl (no dwell delay);
  // fall back to the committed targetEl so the range stays lit while the
  // cursor is over the open popover.
  const activeEl = hover.hoverEl ?? hover.targetEl;
  const hoveredOverlayId = useMemo(() => {
    if (!activeEl || !showOverlays) return null;
    if (overlayIdAttr) {
      return activeEl.getAttribute(overlayIdAttr);
    }
    const idx = Number(activeEl.getAttribute(spanIndexAttr));
    if (Number.isNaN(idx)) return null;
    return findRangeIdForIndex(idx, overlayRanges);
  }, [activeEl, showOverlays, overlayRanges, spanIndexAttr, overlayIdAttr]);

  const renderOverlay = useCallback((): React.ReactNode | null => {
    if (!hover.targetEl) return null;

    // Redline: the hovered span carries the overlay id directly; quotes/clips
    // don't render here so the popover is overlay-only.
    if (overlayIdAttr) {
      const overlayId = hover.targetEl.getAttribute(overlayIdAttr);
      if (!overlayId) return null;
      return renderPopover({
        anchorRect: hover.targetEl.getBoundingClientRect(),
        anchorElement: hover.targetEl,
        quoteId: null,
        clipId: null,
        overlayId,
        onMouseEnter: hover.cancelClose,
        onMouseLeave: hover.armClose,
        onAdjustRange: onBeginAdjust
          ? () => {
              hover.dismiss();
              onBeginAdjust(overlayId);
            }
          : undefined,
      });
    }

    const idx = Number(hover.targetEl.getAttribute(spanIndexAttr));
    if (Number.isNaN(idx)) return null;

    const quoteId = showQuotes ? findRangeIdForIndex(idx, quoteRanges) : null;
    const clipId = showClips ? findRangeIdForIndex(idx, clipRanges) : null;
    const overlayId = showOverlays
      ? findRangeIdForIndex(idx, overlayRanges)
      : null;
    const savedSegmentId = showSavedSegments
      ? findRangeIdForIndex(idx, savedSegmentRanges)
      : null;

    if (!quoteId && !clipId && !overlayId && !savedSegmentId) return null;

    const anchorRect = hover.targetEl.getBoundingClientRect();

    return renderPopover({
      anchorRect,
      anchorElement: hover.targetEl,
      quoteId,
      clipId,
      overlayId,
      savedSegmentId,
      onMouseEnter: hover.cancelClose,
      onMouseLeave: hover.armClose,
      onAdjustRange:
        overlayId && onBeginAdjust
          ? () => {
              // Close the hover pill so its close-grace timer can't fight
              // the drag handles, then enter adjust mode.
              hover.dismiss();
              onBeginAdjust(overlayId);
            }
          : undefined,
    });
  }, [
    hover.targetEl,
    hover.dismiss,
    hover.cancelClose,
    hover.armClose,
    showQuotes,
    showClips,
    showOverlays,
    showSavedSegments,
    quoteRanges,
    clipRanges,
    overlayRanges,
    savedSegmentRanges,
    renderPopover,
    spanIndexAttr,
    overlayIdAttr,
    onBeginAdjust,
  ]);

  return useMemo(
    () => ({
      onMouseMove: hover.onMouseMove,
      onMouseDown: hover.onMouseDown,
      onMouseUp: hover.onMouseUp,
      onMouseLeave: hover.onMouseLeave,
      renderOverlay,
      hoveredOverlayId,
    }),
    [
      hover.onMouseMove,
      hover.onMouseDown,
      hover.onMouseUp,
      hover.onMouseLeave,
      hoveredOverlayId,
      renderOverlay,
    ],
  );
}
